import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import api from '../api/axios';
import { issueStatusLabel } from '../constants/issueStatuses';

// ─── Constants ────────────────────────────────────────────────────────────────

const PRIORITY_META = {
  Blocker:  { color: '#dc2626', bg: '#fef2f2', dot: '#dc2626' },
  Critical: { color: '#ea580c', bg: '#fff7ed', dot: '#f97316' },
  High:     { color: '#d97706', bg: '#fefce8', dot: '#eab308' },
  Low:      { color: '#64748b', bg: '#f8fafc', dot: '#94a3b8' },
  _default: { color: '#64748b', bg: '#f8fafc', dot: '#94a3b8' },
};

const STATUS_META = {
  'Open':             { bg: '#e0e7ff', color: '#3730a3' },
  'In Progress':      { bg: '#fef9c3', color: '#854d0e' },
  'Testing':          { bg: '#f3e8ff', color: '#6b21a8' },
  'Blocked':          { bg: '#fee2e2', color: '#991b1b' },
  'Fixed':            { bg: '#dcfce7', color: '#166534' },
  'Closed':           { bg: '#f3f4f6', color: '#6b7280' },
  'Not Reproducible': { bg: '#f3f4f6', color: '#374151' },
  'Invalid':          { bg: '#fef3c7', color: '#854d0e' },
  "Won't Fix":        { bg: '#fce7f3', color: '#9d174d' },
  'Duplicate':        { bg: '#ede9fe', color: '#5b21b6' },
  'Reopened':         { bg: '#fef9c3', color: '#b45309' },
};

const ROLE_META = {
  superadmin:     { color: '#7c3aed', bg: '#ede9fe', label: 'Super Admin' },
  admin:          { color: '#2C63AE', bg: '#EAF6FD', label: 'Admin' },
  developer:      { color: '#0891b2', bg: '#e0f2fe', label: 'Developer' },
  tester:         { color: '#059669', bg: '#d1fae5', label: 'QA / Tester' },
  manager:        { color: '#d97706', bg: '#fef3c7', label: 'Manager' },
  product_manager:{ color: '#7c3aed', bg: '#ede9fe', label: 'Product Manager' },
};

const TYPE_ICON = { Bug: 'Bug', Task: 'Task', Story: 'Story', Epic: 'Epic' };

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const daysFromNow = (d) => {
  if (!d) return null;
  const diff = Math.ceil((new Date(d) - Date.now()) / 86400000);
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff === 0) return 'Today';
  return `in ${diff}d`;
};
const avatarInitial = (name) => (name || '?').charAt(0).toUpperCase();
const avatarColor = (name) => {
  const colors = ['#1272B3','#0891b2','#059669','#7c3aed','#db2777','#d97706','#dc2626'];
  let h = 0;
  for (let c of (name || '')) h = (h * 31 + c.charCodeAt(0)) % colors.length;
  return colors[h];
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatCard = ({ label, value, color, sub }) => (
  <div style={{ background: '#fff', borderRadius: 12, padding: '1rem 1.25rem',
    border: '1px solid #e2e8f0', textAlign: 'center' }}>
    <div style={{ fontSize: '1.8rem', fontWeight: 800, color: color || '#1e293b' }}>{value ?? '—'}</div>
    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginTop: 2 }}>{label}</div>
    {sub && <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
  </div>
);

const PBadge = ({ priority }) => {
  const m = PRIORITY_META[priority] || PRIORITY_META._default;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
      background: m.bg, color: m.color, padding: '1px 8px', borderRadius: 20,
      fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.dot }} />
      {priority}
    </span>
  );
};

const SBadge = ({ status }) => {
  const m = STATUS_META[status] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{ background: m.bg, color: m.color, padding: '2px 8px',
      borderRadius: 20, fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {issueStatusLabel(status)}
    </span>
  );
};

const Section = ({ title, children, action }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
      <h3 style={{ margin: 0, fontSize: '0.8rem', fontWeight: 800, color: '#374151',
        textTransform: 'uppercase', letterSpacing: '0.07em' }}>{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const EmployeeProfile = () => {
  const { id } = useParams();
  const { role } = useAuth();
  const currentYear = new Date().getFullYear();
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState(1);
  
  // Check if current user can view performance section
  const canViewPerformance = ['superadmin', 'admin', 'manager'].includes(role?.toLowerCase());

  const { data, isLoading, error } = useQuery({
    queryKey: ['employee-profile', id, filterPeriod, selectedYear, selectedQuarter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filterPeriod !== 'all') {
        params.append('period', filterPeriod);
        params.append('year', selectedYear);
        if (filterPeriod === 'quarter') {
          params.append('quarter', selectedQuarter);
        }
      }
      return api.get(`/users/${id}/profile?${params.toString()}`).then(r => r.data);
    },
    staleTime: 60000,
  });

  if (isLoading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: '#64748b', fontSize: '1rem' }}>
      Loading profile…
    </div>
  );

  if (error) return (
    <div style={{ padding: '3rem', textAlign: 'center', color: '#dc2626' }}>
      Failed to load profile.
    </div>
  );

  const { user, stats, current_work, past_work, projects, sprint_timeline, estimated_free_date } = data;
  const roleMeta = ROLE_META[user.role?.toLowerCase()] || { color: '#374151', bg: '#f3f4f6', label: user.role };
  const ac = avatarColor(user.full_name || user.email);
  const isAvailableNow = current_work.length === 0;
  const freeLabel = isAvailableNow
    ? 'Available now'
    : estimated_free_date
      ? `Free ~${fmt(estimated_free_date)} (${daysFromNow(estimated_free_date)})`
      : `${current_work.length} open item${current_work.length > 1 ? 's' : ''}, no sprint deadline`;

  return (
    <div style={{ padding: '1.75rem 2rem', background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>

      {/* ── Profile Header ── */}
      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0',
        padding: '1.75rem 2rem', marginBottom: 24,
        display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* Avatar */}
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: ac,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.8rem', fontWeight: 800, color: '#fff', flexShrink: 0 }}>
          {avatarInitial(user.full_name || user.email)}
        </div>

        {/* Name + role + meta */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' }}>
              {user.full_name || user.email}
            </h1>
            <span style={{ background: roleMeta.bg, color: roleMeta.color,
              padding: '2px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 700 }}>
              {roleMeta.label}
            </span>
          </div>
          <div style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: 8 }}>{user.email}</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.8rem', color: '#64748b' }}>
            {user.team && <span>Team: <strong>{user.team}</strong></span>}
            <span>Projects: <strong>{projects.length}</strong></span>
          </div>
        </div>

        {/* Availability badge */}
        <div style={{
          padding: '0.75rem 1.25rem', borderRadius: 12, textAlign: 'center',
          background: isAvailableNow ? '#f0fdf4' : estimated_free_date ? '#fffbeb' : '#fef2f2',
          border: `1px solid ${isAvailableNow ? '#bbf7d0' : estimated_free_date ? '#fde68a' : '#fecaca'}`,
          minWidth: 180,
        }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b',
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
            Availability
          </div>
          <div style={{ fontSize: '0.85rem', fontWeight: 700,
            color: isAvailableNow ? '#16a34a' : estimated_free_date ? '#b45309' : '#dc2626' }}>
            {freeLabel}
          </div>
          {!isAvailableNow && (
            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 3 }}>
              {current_work.length} active item{current_work.length > 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      {/* ── Stats Row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 24 }}>
        <StatCard label="Total Assigned" value={stats.total_assigned} color="#1272B3" />
        <StatCard label="Completed" value={stats.total_closed} color="#16a34a"
          sub={stats.story_points_done > 0 ? `${stats.story_points_done} pts` : null} />
        <StatCard label="In Progress" value={stats.in_progress_count} color="#d97706" />
        <StatCard label="Open" value={stats.open_count} color="#64748b" />
        <StatCard label="Blockers Open" value={stats.blockers_open} color="#dc2626" />
        <StatCard label="Avg Fix Time" value={stats.avg_days_to_close ? `${stats.avg_days_to_close}d` : '—'} color="#0891b2" sub="days per issue" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>

        {/* ── Left column ── */}
        <div>

          {/* Current Work */}
          <Section title={`Currently Working On (${current_work.length})`}>
            {current_work.length === 0 ? (
              <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '1.5rem', textAlign: 'center',
                color: '#16a34a', fontWeight: 600, fontSize: '0.88rem' }}>
                No active work. Ready for next task.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {current_work.map(i => {
                  const pm = PRIORITY_META[i.priority] || PRIORITY_META._default;
                  return (
                    <div key={i.issueid} style={{
                      background: '#fff', borderRadius: 10, padding: '0.85rem 1rem',
                      border: '1px solid #e2e8f0', borderLeft: `4px solid ${pm.dot}`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 600 }}>
                              {TYPE_ICON[i.issuetype]} #{i.issueid}
                            </span>
                            <PBadge priority={i.priority} />
                            <SBadge status={i.status} />
                          </div>
                          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a', lineHeight: 1.4 }}>
                            {i.description || '(No description)'}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 4 }}>
                            {i.projectname}{i.sprint_name && ` · ${i.sprint_name}`}
                          </div>
                        </div>
                        {i.due_date && (
                          <div style={{ fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap',
                            color: new Date(i.due_date) < Date.now() ? '#dc2626' : '#64748b',
                            textAlign: 'right' }}>
                            📅 {fmt(i.due_date)}
                            <div style={{ fontSize: '0.65rem', color: '#9ca3af' }}>{daysFromNow(i.due_date)}</div>
                          </div>
                        )}
                      </div>
                      {i.sprint_end && (
                        <div style={{ marginTop: 6, fontSize: '0.7rem', color: '#64748b', background: '#f8fafc',
                          borderRadius: 6, padding: '3px 8px', display: 'inline-block' }}>
                          ⚡ Sprint ends {fmt(i.sprint_end)} · {daysFromNow(i.sprint_end)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Projects */}
          <Section title={`Projects (${projects.length})`}>
            {projects.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Not assigned to any projects.</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {projects.map(p => (
                  <div key={p.projectid} style={{
                    padding: '5px 12px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 600,
                    background: p.status === 'Active' ? '#EAF6FD' : '#f3f4f6',
                    color: p.status === 'Active' ? '#1272B3' : '#64748b',
                    border: '1px solid',
                    borderColor: p.status === 'Active' ? '#a9ddf5' : '#e2e8f0',
                  }}>
                    {p.projectname}
                    <span style={{ marginLeft: 6, fontSize: '0.65rem', opacity: 0.7 }}>{p.status}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>

        {/* ── Right column ── */}
        <div>

          {/* Sprint Timeline */}
          <Section title="📅 Sprint Timeline">
            {sprint_timeline.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No sprint history yet.</div>
            ) : (
              <div style={{ position: 'relative' }}>
                {sprint_timeline.map((s, idx) => {
                  const done = Number(s.done);
                  const total = Number(s.total);
                  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                  const statusColor = {
                    Active: '#16a34a', Planning: '#d97706',
                    Released: '#2C63AE', Review: '#7c3aed', Closed: '#64748b'
                  }[s.status] || '#64748b';

                  return (
                    <div key={s.id} style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                      {/* Timeline dot + line */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20 }}>
                        <div style={{ width: 12, height: 12, borderRadius: '50%', background: statusColor, flexShrink: 0, marginTop: 3 }} />
                        {idx < sprint_timeline.length - 1 && (
                          <div style={{ flex: 1, width: 2, background: '#e2e8f0', minHeight: 20, marginTop: 4 }} />
                        )}
                      </div>
                      {/* Content */}
                      <div style={{ flex: 1, background: '#fff', borderRadius: 10, padding: '0.7rem 0.9rem',
                        border: '1px solid #e2e8f0', marginBottom: 2 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontWeight: 700, fontSize: '0.83rem', color: '#0f172a' }}>{s.sprint_name}</span>
                          <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '1px 7px',
                            borderRadius: 20, background: `${statusColor}18`, color: statusColor }}>
                            {s.status}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 6 }}>
                          {s.projectname} · {fmt(s.start_date)} → {fmt(s.end_date)}
                        </div>
                        {/* Progress bar */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 5, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: statusColor, borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap' }}>
                            {done}/{total} done
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Past Work */}
          <Section 
            title={`Past Work (${past_work.length})`}
            action={
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {/* Period Filter */}
                <select
                  value={filterPeriod}
                  onChange={(e) => setFilterPeriod(e.target.value)}
                  style={{
                    padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0',
                    fontSize: '0.75rem', fontWeight: 600, color: '#475569',
                    background: '#fff', cursor: 'pointer'
                  }}
                >
                  <option value="all">All Time</option>
                  <option value="year">By Year</option>
                  <option value="quarter">By Quarter</option>
                </select>

                {/* Year Selector */}
                {filterPeriod !== 'all' && (
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    style={{
                      padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0',
                      fontSize: '0.75rem', fontWeight: 600, color: '#475569',
                      background: '#fff', cursor: 'pointer'
                    }}
                  >
                    {Array.from({ length: 5 }, (_, i) => currentYear - i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                )}

                {/* Quarter Selector */}
                {filterPeriod === 'quarter' && (
                  <select
                    value={selectedQuarter}
                    onChange={(e) => setSelectedQuarter(parseInt(e.target.value))}
                    style={{
                      padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0',
                      fontSize: '0.75rem', fontWeight: 600, color: '#475569',
                      background: '#fff', cursor: 'pointer'
                    }}
                  >
                    <option value="1">Q1 (Jan-Mar)</option>
                    <option value="2">Q2 (Apr-Jun)</option>
                    <option value="3">Q3 (Jul-Sep)</option>
                    <option value="4">Q4 (Oct-Dec)</option>
                  </select>
                )}
              </div>
            }
          >
            {past_work.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                {filterPeriod === 'all' 
                  ? 'No completed work yet.' 
                  : `No completed work for ${filterPeriod === 'year' ? selectedYear : `Q${selectedQuarter} ${selectedYear}`}.`}
              </div>
            ) : (
              <div style={{ maxHeight: 380, overflowY: 'auto', paddingRight: 4 }}>
                {past_work.map(i => (
                  <div key={i.issueid} style={{
                    display: 'flex', gap: 10, alignItems: 'flex-start',
                    padding: '0.6rem 0', borderBottom: '1px solid #f1f5f9',
                  }}>
                    <div style={{ fontSize: '0.85rem', flexShrink: 0, marginTop: 1 }}>
                      {TYPE_ICON[i.issuetype] || '📌'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.8rem', color: '#374151', fontWeight: 500,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {i.description || '(No description)'}
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                        <PBadge priority={i.priority} />
                        <SBadge status={i.status} />
                        <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{i.projectname}</span>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '0.7rem', color: '#16a34a', fontWeight: 600 }}>
                        {fmt(i.closeddate)}
                      </div>
                      {i.days_taken != null && (
                        <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                          {Math.round(i.days_taken)}d taken
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

        </div>
      </div>

      {/* ── Appraisal placeholder (Manager/Admin only) ── */}
      {canViewPerformance && (
        <div style={{ marginTop: 8, background: '#fff', borderRadius: 16, border: '1px dashed #c7d2fe',
          padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ fontSize: '2rem' }}>📊</div>
          <div>
            <div style={{ fontWeight: 700, color: '#1272B3', fontSize: '0.9rem' }}>
              Performance & Appraisal — Coming Soon
            </div>
            <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>
              Manager ratings, FY summary, KPIs and appraisal notes will be available here.
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default EmployeeProfile;
